/**
 * search-rescue.js — client-side "Did you mean" rescue for zero-result
 * Shopify storefront searches. Two independent surfaces, both gated on a
 * confirmed zero-result state from Shopify's own native search:
 *   1. The full /search results page.
 *   2. The header's live-typing predictive search dropdown.
 * Never intercepts or replaces normal search. Fails silently on any error.
 *
 * Install: see README.md.
 */
(function () {
  'use strict';

  // ---- CONFIG: theme-specific selectors. ----
  // Set for the Stockton Modeller theme, confirmed identical on both the
  // live theme (Weblegs, #178089099587) and staging (RC Cars Update
  // 19/06/2026, #182464315715) as of 2026-07-23.
  var CONFIG = {
    // --- Full results page (sections/main-search.liquid) ---
    // Present whenever the search results section renders, whether or not
    // it found anything. If this selector matches nothing, we can't tell
    // what state the page is in, so we do nothing.
    resultsContainerSelector: '.yv-search-page-container',
    // Individual product result cards inside the container above — the
    // theme wraps each hit in a div with this attribute.
    resultItemSelector: '[data-product-grid]',
    // The theme's own "no results" message (snippets/not-found.liquid) —
    // insert suggestions right after it if present, otherwise append to
    // the container.
    insertAfterSelector: '.nodatafound_wrapper',

    // --- Header live predictive search ---
    // Populated by assets/search.js via AJAX against Shopify's predictive
    // search endpoint, wholesale-replaced on every debounced keystroke.
    // This theme has two separate, coexisting search bar components (used
    // at different breakpoints/nav patterns) with their own container +
    // input pair — both need hooking, or the one not covered silently
    // never gets a rescue at all despite looking identical to the other.
    drawerSurfaces: [
      // Modal drawer (snippets/search-drawer.liquid), section_id=predictive-search.
      {
        resultsSelector: '.yv-search-result-container',
        queryInputSelector: '#search-drawer-query-input',
      },
      // Always-visible header search bar (snippets/header-4.liquid),
      // section_id=predictive-search-lite — routed differently in
      // assets/search.js because this input has the yv-search-bar class.
      {
        resultsSelector: '.yv-header-searchbar-content',
        queryInputSelector: '#search-drawer-input-query',
      },
    ],
    // Marks a debounced keystroke that came back with zero real matches —
    // same class in both predictive-search.liquid and
    // predictive-search-lite.liquid's else branch.
    drawerNoResultsSelector: '.search-bar-no-results',

    // --- Shared ---
    // Id given to the rendered suggestions block.
    suggestionsContainerId: 'search-rescue-suggestions',
    // Cap how many suggestion cards render — a handful of ambiguous
    // lookup keys resolve to a dozen+ products; past ~4 it stops reading
    // as "did you mean" and starts looking like a broken results list.
    maxSuggestions: 4,
  };
  // ------------------------------------------------------------------

  // Markup below reuses the theme's own predictive-search CSS classes
  // (sections/predictive-search-lite.liquid) so suggestion cards inherit
  // the site's existing product-thumbnail styling for free, rather than
  // shipping bespoke CSS. Store is GBP-only (see project scope), so price
  // formatting is hardcoded to £ rather than reading a shop-wide setting.
  function formatMoneyGBP(cents) {
    return '£' + (cents / 100).toFixed(2);
  }

  // The map only carries {handle, title} — image and current price come
  // from Shopify's public per-product AJAX endpoint, fetched only for the
  // handful of suggestions actually being shown. If this fails for a given
  // product, that card just keeps its plain title link — the rest of the
  // page, and the rest of the suggestions, are unaffected.
  function enrichSuggestion(li, handle) {
    fetch('/products/' + handle + '.js')
      .then(function (res) {
        if (!res.ok) throw new Error('bad response');
        return res.json();
      })
      .then(function (product) {
        var imgSlot = li.querySelector('[data-rescue-image-slot]');
        if (imgSlot && product.featured_image) {
          var img = document.createElement('img');
          img.src = product.featured_image;
          img.alt = '';
          img.loading = 'lazy';
          img.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;object-fit:cover;';
          imgSlot.appendChild(img);
        }
        var priceSlot = li.querySelector('[data-rescue-price-slot]');
        if (priceSlot && typeof product.price === 'number') {
          priceSlot.textContent = formatMoneyGBP(product.price);
        }
      })
      .catch(function () {
        // fail silently: card keeps its plain title-only link
      });
  }

  // Must be captured synchronously during initial script execution —
  // document.currentScript is null once we're inside an async callback.
  var SELF_SCRIPT = document.currentScript || document.querySelector('script[data-map]');

  function getQuery() {
    try {
      var params = new URLSearchParams(window.location.search);
      var q = params.get('q');
      return q ? q.trim() : '';
    } catch (e) {
      return '';
    }
  }

  // 'has-results' | 'zero-results' | 'unknown'
  function detectState() {
    try {
      var container = document.querySelector(CONFIG.resultsContainerSelector);
      if (!container) return 'unknown';
      var items = container.querySelectorAll(CONFIG.resultItemSelector);
      return items.length > 0 ? 'has-results' : 'zero-results';
    } catch (e) {
      return 'unknown';
    }
  }

  // Must be byte-identical to norm() in build_code_map.py — see
  // tests/test_norm_parity.py.
  function norm(s) {
    return String(s).toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  // Must be byte-identical to norm_loose() in build_code_map.py.
  function normLoose(s) {
    var n = norm(s);
    if (/^[0-9]+$/.test(n)) {
      var stripped = n.replace(/^0+/, '');
      return stripped || '0';
    }
    return n;
  }

  // Must be byte-identical to candidate_keys() in build_code_map.py.
  function candidateKeys(query) {
    var words = query.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return [];
    var full = words.join(' ');
    var keys = {};
    keys[norm(full)] = true;
    keys[normLoose(full)] = true;
    if (words.length > 1) {
      var rest = words.slice(1).join(' ');
      keys[norm(rest)] = true;
      keys[normLoose(rest)] = true;
    }
    delete keys[''];
    return Object.keys(keys);
  }

  function track(eventName, detail) {
    try {
      if (window.clarity) window.clarity('event', eventName, detail);
    } catch (e) {
      // analytics failures must never break the page
    }
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName }, detail));
    } catch (e) {
      // analytics failures must never break the page
    }
  }

  // Fetched once per page view and cached — both surfaces share it, and
  // the drawer can trigger this repeatedly as someone types. A failed
  // fetch caches a null result rather than retrying on every keystroke;
  // a page reload is what retries it.
  var mapPromise = null;
  function getMap() {
    if (mapPromise) return mapPromise;
    if (!SELF_SCRIPT) return Promise.resolve(null);
    var mapUrl = SELF_SCRIPT.getAttribute('data-map');
    if (!mapUrl) return Promise.resolve(null);
    mapPromise = fetch(mapUrl)
      .then(function (res) {
        if (!res.ok) throw new Error('bad response');
        return res.json();
      })
      .then(function (data) {
        if (!data || !Array.isArray(data.products) || !data.keys) return null;
        return data;
      })
      .catch(function () {
        return null;
      });
    return mapPromise;
  }

  function matchQuery(query, mapData) {
    var indices = {};
    candidateKeys(query).forEach(function (key) {
      var hit = mapData.keys[key];
      if (Array.isArray(hit)) {
        hit.forEach(function (i) {
          indices[i] = true;
        });
      }
    });
    return Object.keys(indices)
      .map(function (i) {
        return mapData.products[Number(i)];
      })
      .filter(Boolean);
  }

  // The header drawer's dropdown has a fixed-height/overflow:hidden layout
  // at some breakpoints — fine for its own short "no results" message, but
  // it silently clips anything appended below that message. Only the
  // zero-results rescue path ever calls this, so forcing these open can't
  // affect the drawer's normal (non-empty) scroll behaviour for real
  // results. !important is needed since the theme's own rules aren't
  // otherwise beatable from an inline style.
  function unclip(container) {
    ['overflow', 'overflow-y', 'max-height'].forEach(function (prop) {
      container.style.setProperty(prop, prop === 'max-height' ? 'none' : 'visible', 'important');
    });
    var parent = container.parentElement;
    if (parent) {
      ['overflow', 'overflow-y', 'max-height'].forEach(function (prop) {
        parent.style.setProperty(prop, prop === 'max-height' ? 'none' : 'visible', 'important');
      });
    }
  }

  // anchorNode: insert the suggestions block right after this node if
  // given, otherwise append to the end of container. anchorNode is also
  // hidden (not removed — insertBefore below still needs it as a
  // reference point) once we actually have suggestions to show, so
  // Shopify's own "no results" message doesn't sit right above ours
  // reading as a contradiction.
  function renderSuggestionsInto(container, anchorNode, matches, query) {
    if (anchorNode) {
      anchorNode.style.display = 'none';
    }

    var shown = matches.slice(0, CONFIG.maxSuggestions);

    var wrapper = document.createElement('div');
    wrapper.id = CONFIG.suggestionsContainerId;
    wrapper.className = 'searchbar-content-list';

    var heading = document.createElement('h6');
    heading.className = 'searchbar-list-head text-small';
    heading.textContent = 'Did you mean:';
    wrapper.appendChild(heading);

    var list = document.createElement('ul');
    list.className = 'list-unstyled searchbar-product-list searchbar-list';

    shown.forEach(function (product) {
      var li = document.createElement('li');
      li.className = 'searchbar-item searchbar-product-item';

      var card = document.createElement('div');
      card.className = 'searchbar-product-card';

      var imgLink = document.createElement('a');
      imgLink.className = 'searchbar-product-img';
      imgLink.href = '/products/' + product.handle;
      var imgWrapper = document.createElement('div');
      imgWrapper.className = 'image-wrapper';
      imgWrapper.setAttribute('data-rescue-image-slot', '');
      imgLink.appendChild(imgWrapper);
      card.appendChild(imgLink);

      var info = document.createElement('div');
      info.className = 'searchbar-product-info';
      var titleLink = document.createElement('a');
      titleLink.className = 'yv-product-title';
      titleLink.href = '/products/' + product.handle;
      titleLink.textContent = product.title; // textContent only — titles are untrusted input
      info.appendChild(titleLink);

      var priceBox = document.createElement('div');
      priceBox.className = 'yv-prizebox';
      var priceSpan = document.createElement('span');
      priceSpan.className = 'yv-product-price';
      priceSpan.setAttribute('data-rescue-price-slot', '');
      priceBox.appendChild(priceSpan);
      info.appendChild(priceBox);

      card.appendChild(info);
      li.appendChild(card);
      list.appendChild(li);

      [imgLink, titleLink].forEach(function (a) {
        a.addEventListener('click', function () {
          track('search_rescue_click', { query: query, handle: product.handle });
        });
      });

      enrichSuggestion(li, product.handle);
    });

    wrapper.appendChild(list);

    if (anchorNode && anchorNode.parentNode) {
      anchorNode.parentNode.insertBefore(wrapper, anchorNode.nextSibling);
    } else {
      container.appendChild(wrapper);
    }
  }

  function initFullPage() {
    var query = getQuery();
    if (!query) return;

    if (detectState() !== 'zero-results') return;

    var container = document.querySelector(CONFIG.resultsContainerSelector);
    if (!container) return;

    getMap().then(function (mapData) {
      if (!mapData) return;

      var matches = matchQuery(query, mapData);
      if (matches.length === 0) {
        track('search_rescue_empty', { query: query, surface: 'results-page' });
        return;
      }

      var anchor = document.querySelector(CONFIG.insertAfterSelector);
      renderSuggestionsInto(container, anchor, matches, query);
      track('search_rescue_shown', {
        query: query,
        count: Math.min(matches.length, CONFIG.maxSuggestions),
        surface: 'results-page',
      });
    });
  }

  // The drawer wholesale-replaces its own container's innerHTML on every
  // debounced keystroke (loading state, real results, or a no-results
  // message) — so there's never stale content to clean up ourselves, and
  // checking whether our own suggestions block is already present is a
  // sufficient re-entrancy guard against our own insertion re-triggering
  // this observer.
  function handleDrawerMutation(container, queryInputSelector) {
    try {
      if (container.querySelector('#' + CONFIG.suggestionsContainerId)) return;

      var noResults = container.querySelector(CONFIG.drawerNoResultsSelector);
      if (!noResults) return;

      var input = document.querySelector(queryInputSelector);
      var query = input && input.value ? input.value.trim() : '';
      if (!query) return;

      getMap().then(function (mapData) {
        if (!mapData) return;
        // Stale: the user kept typing and the drawer already moved on to
        // a newer state by the time the (usually cached) map resolved.
        if (!container.contains(noResults)) return;

        var matches = matchQuery(query, mapData);
        if (matches.length === 0) {
          track('search_rescue_empty', { query: query, surface: 'drawer' });
          return;
        }

        unclip(container);
        renderSuggestionsInto(container, noResults, matches, query);
        track('search_rescue_shown', {
          query: query,
          count: Math.min(matches.length, CONFIG.maxSuggestions),
          surface: 'drawer',
        });
      });
    } catch (e) {
      // fail silently
    }
  }

  function initDrawerObserver(surface) {
    try {
      var container = document.querySelector(surface.resultsSelector);
      if (!container) return; // this surface isn't present on this page — do nothing
      var observer = new MutationObserver(function () {
        handleDrawerMutation(container, surface.queryInputSelector);
      });
      observer.observe(container, { childList: true });
    } catch (e) {
      // fail silently
    }
  }

  function init() {
    initFullPage();
    CONFIG.drawerSurfaces.forEach(initDrawerObserver);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
